import React, { useState, useEffect } from "react";
import { supabase } from "@/auth/AuthContext";

const GOLD = "#c9a86a";
const PARTNER_EMAIL = "alessandro94marchi@gmail.com";

function Chip({ children }) {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] tracking-[0.18em] uppercase text-neutral-700"
      style={{ border: "1px solid rgba(201,168,106,0.3)", background: "#fafaf8" }}
    >
      <span aria-hidden className="w-1 h-1 rounded-full" style={{ background: GOLD }} />
      {children}
    </span>
  );
}

function Check() {
  return (
    <svg viewBox="0 0 24 24" className="w-[14px] h-[14px] flex-none" fill="none"
      stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12l5 5L20 6" />
    </svg>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-neutral-200 py-5">
      <button type="button" className="w-full flex items-start justify-between gap-4 text-left"
        aria-expanded={open} onClick={() => setOpen(v => !v)}>
        <span className="font-display text-[16px] font-medium text-neutral-900">{q}</span>
        <span className="text-[20px] text-neutral-400 mt-0.5 flex-none">{open ? "−" : "+"}</span>
      </button>
      {open && <p className="mt-3 text-[14px] text-neutral-600 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function PartnerPage() {
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", type: "supplier", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    document.title = "Diventa Partner WedFlow — Guadagna con ogni matrimonio";
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.email) {
      setFormError("Compila tutti i campi obbligatori.");
      return;
    }
    setFormError("");
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("partner-inquiry", {
        body: form,
      });
      if (error) {
        setFormError(`Errore nell'invio. Scrivici a ${PARTNER_EMAIL}.`);
        return;
      }
      setSubmitted(true);
    } catch {
      setFormError(`Errore di connessione. Scrivici a ${PARTNER_EMAIL}.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-neutral-200">
        <div className="max-w-[1240px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 select-none">
            <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: GOLD }} />
            <span className="font-display tracking-[0.28em] text-[13px] font-medium text-neutral-950">WEDFLOW</span>
          </a>
          <a href="/login" className="text-[12px] tracking-[0.12em] uppercase text-neutral-500 hover:text-neutral-900 transition-colors">
            Accedi
          </a>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="pt-20 md:pt-32 pb-16 md:pb-24 bg-neutral-950 text-white text-center px-5">
          <div className="max-w-[760px] mx-auto">
            <Chip>Programma Partner</Chip>
            <h1 className="font-display mt-7 text-[40px] sm:text-[56px] md:text-[72px] leading-[1.02] tracking-[-0.03em] font-light">
              Guadagna con ogni<br />
              <span className="italic" style={{ color: GOLD }}>matrimonio</span>
            </h1>
            <p className="mt-6 text-[15px] md:text-[17px] text-neutral-300 max-w-lg mx-auto leading-relaxed">
              Sei un DJ, un fotografo, un wedding planner o una location?
              Unisciti al programma partner e trasforma ogni matrimonio in un'opportunità.
            </p>
            {/* CTA primaria */}
            <div className="mt-10 flex justify-center">
              <a href="#candidati"
                className="rounded-full px-8 py-4 text-[14px] tracking-wide font-medium text-neutral-950 transition-all hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#c9a86a]"
                style={{ background: GOLD }}>
                Candidati ora →
              </a>
            </div>

            {/* Azioni secondarie — brochure impilate, stesso asse */}
            <div className="mt-8 mx-auto w-full max-w-[280px] flex flex-col items-center gap-4">
              <div className="w-full flex flex-col items-center gap-1.5">
                <a href="/WedFlow-Brochure-Partner.pdf" download
                  className="w-full text-center rounded-full px-5 py-2 text-[11px] tracking-[0.08em] uppercase text-neutral-400 border border-neutral-700 hover:border-neutral-500 hover:text-neutral-300 transition-all">
                  ↓ Brochure partner
                </a>
                <span className="text-[10px] tracking-[0.06em] text-neutral-600">Per fornitori e location</span>
              </div>
              <div className="w-full flex flex-col items-center gap-1.5">
                <a href="/WedFlow-Brochure-Sposi.pdf" download
                  className="w-full text-center rounded-full px-5 py-2 text-[11px] tracking-[0.08em] uppercase text-neutral-400 border border-neutral-700 hover:border-neutral-500 hover:text-neutral-300 transition-all">
                  ↓ Brochure per i tuoi sposi
                </a>
                <span className="text-[10px] tracking-[0.06em] text-neutral-600">Da mostrare agli sposi</span>
              </div>
            </div>
          </div>
        </section>

        {/* Due modelli */}
        <section className="py-20 md:py-28 px-5">
          <div className="max-w-[1240px] mx-auto">
            <div className="text-center mb-14">
              <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 mb-5">
                <span className="inline-block w-6 h-px align-middle mr-3" style={{ background: GOLD }} />
                Come funziona
              </div>
              <h2 className="font-display text-[34px] sm:text-[44px] tracking-[-0.02em] font-light text-neutral-950">
                Scegli il tuo modello
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Card Fornitore */}
              <div className="rounded-[14px] border border-neutral-200 p-8 md:p-10 flex flex-col">
                <div className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-4">Per fornitori</div>
                <h3 className="font-display text-[24px] tracking-[-0.01em] font-medium text-neutral-950">Programma Referral</h3>
                <p className="mt-3 text-[14px] text-neutral-600 leading-relaxed">
                  Sei un DJ, un fotografo o un wedding planner?<br />
                  <strong className="text-neutral-800">Guadagni tu su ogni vendita</strong> — condividi il tuo codice e ricevi una commissione automatica ogni volta che un cliente sceglie WedFlow.
                </p>
                <div className="my-8 h-px bg-neutral-200" />
                <ul className="flex flex-col gap-3 flex-1">
                  {[
                    "Codice sconto personale (−5% per i tuoi clienti)",
                    "20% di commissione su ogni vendita generata",
                    "Bonifico automatico il 1° del mese su Stripe",
                    "Dashboard Stripe per monitorare i guadagni",
                    "Zero vincoli, zero costi di attivazione",
                  ].map(f => (
                    <li key={f} className="flex items-start gap-3 text-[14px] text-neutral-800">
                      <span className="mt-0.5"><Check /></span>{f}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-8 border-t border-neutral-200">
                  <div className="font-display text-[40px] font-light tracking-[-0.03em]">Gratuito</div>
                  <div className="text-[12px] tracking-[0.15em] uppercase text-neutral-500 mt-1">Nessun costo di attivazione</div>
                </div>
              </div>

              {/* Card Location */}
              <div className="rounded-[14px] p-8 md:p-10 flex flex-col text-white"
                style={{ background: "#0a0a0a", boxShadow: "0 40px 100px -40px rgba(10,10,10,0.45), inset 0 0 0 1px rgba(201,168,106,0.3)" }}>
                <div className="text-[11px] tracking-[0.22em] uppercase mb-4" style={{ color: GOLD }}>Per location</div>
                <h3 className="font-display text-[24px] tracking-[-0.01em] font-medium">Canone Annuale</h3>
                <p className="mt-3 text-[14px] text-neutral-300 leading-relaxed">
                  Gestisci una villa, un castello o un agriturismo?<br />
                  <strong className="text-neutral-100">Offri WedFlow ai tuoi sposi come servizio incluso</strong> — differenziati dai competitor e porta valore ad ogni coppia che sceglie la tua location.
                </p>
                <div className="my-6 h-px bg-neutral-800" />
                <ul className="flex flex-col gap-3">
                  {[
                    "WedFlow Premium incluso in ogni matrimonio",
                    "Proiezione live delle foto degli ospiti (slideshow)",
                    "Reel AI del matrimonio incluso per ogni coppia",
                    "Un servizio in più nei tuoi preventivi",
                    "Fatturabile come spesa aziendale",
                  ].map(f => (
                    <li key={f} className="flex items-start gap-3 text-[14px] text-neutral-100">
                      <span className="mt-0.5"><Check /></span>{f}
                    </li>
                  ))}
                </ul>

                {/* Tier 1 */}
                <div className="mt-8 pt-7 border-t border-neutral-800">
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <div className="text-[11px] tracking-[0.18em] uppercase text-neutral-400">Canone Annuale · fino a 30 eventi/anno</div>
                  </div>
                  {/* Founding price — highlighted */}
                  <div className="mt-3 rounded-[10px] px-4 py-3" style={{ background: "rgba(201,168,106,0.1)", border: "1px solid rgba(201,168,106,0.3)" }}>
                    <div className="flex items-center gap-2">
                      <div className="font-display text-[32px] font-light tracking-[-0.03em]">
                        €890<span className="text-[16px] text-neutral-400">/anno</span>
                      </div>
                      <span className="rounded-full px-2.5 py-0.5 text-[10px] tracking-[0.16em] uppercase font-medium text-neutral-950" style={{ background: GOLD }}>
                        Founding
                      </span>
                    </div>
                    <div className="text-[12px] text-neutral-400 mt-1">Primo anno · poi €1.190/anno · riservato alle prime 10 location</div>
                  </div>
                  {/* Standard price */}
                  <div className="mt-2 flex items-center gap-3 px-1">
                    <div className="text-[13px] text-neutral-500">oppure</div>
                    <div className="font-display text-[20px] font-light text-neutral-400">
                      €1.190<span className="text-[14px]">/anno</span>
                    </div>
                    <div className="text-[12px] text-neutral-600">a listino</div>
                  </div>
                </div>

                {/* Tier 2 */}
                <div className="mt-6 pt-6 border-t border-neutral-800">
                  <div className="text-[11px] tracking-[0.18em] uppercase text-neutral-400 mb-3">Unlimited · eventi illimitati</div>
                  <div className="flex items-baseline gap-3">
                    <div className="font-display text-[28px] font-light tracking-[-0.03em] text-neutral-300">
                      €1.799<span className="text-[14px] text-neutral-500">/anno</span>
                    </div>
                    <div className="text-[12px] text-neutral-600">Per location ad alto volume (45+ eventi/anno)</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabella comparativa */}
            <div className="mt-16 overflow-x-auto">
              <table className="w-full text-[13px] md:text-[14px] text-left border-collapse">
                <thead>
                  <tr>
                    <th className="py-3 pr-6 text-[11px] tracking-[0.2em] uppercase text-neutral-500 font-normal border-b border-neutral-200 w-[30%]"></th>
                    <th className="py-3 px-4 text-neutral-900 font-medium border-b border-neutral-200">Fornitore Referral</th>
                    <th className="py-3 px-4 text-neutral-900 font-medium border-b border-neutral-200">Location — Canone Annuale</th>
                    <th className="py-3 px-4 text-neutral-900 font-medium border-b border-neutral-200">Location — Unlimited</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["A chi si rivolge", "DJ, fotografi, wedding planner", "Ville, castelli, agriturismi (≤30 eventi/anno)", "Location ad alto volume (45+ eventi/anno)"],
                    ["Chi paga", "Nessuno (gratuito)", "La location", "La location"],
                    ["Chi guadagna", "Il fornitore (20% commissione)", "La location (valore incluso nel preventivo)", "La location (valore incluso nel preventivo)"],
                    ["Costo per te", "Gratuito", "€890 primo anno, poi €1.190/anno", "€1.799/anno"],
                    ["Cosa ottengono gli sposi", "5% di sconto su WedFlow", "WedFlow Premium incluso", "WedFlow Premium incluso"],
                  ].map(([label, a, b, c], i) => (
                    <tr key={label} className={i % 2 === 0 ? "bg-neutral-50/60" : ""}>
                      <td className="py-3 pr-6 text-neutral-500 font-medium border-b border-neutral-100">{label}</td>
                      <td className="py-3 px-4 text-neutral-800 border-b border-neutral-100">{a}</td>
                      <td className="py-3 px-4 text-neutral-800 border-b border-neutral-100">{b}</td>
                      <td className="py-3 px-4 text-neutral-800 border-b border-neutral-100">{c}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Come funziona — 3 step */}
        <section className="py-20 md:py-28 px-5 bg-neutral-50">
          <div className="max-w-[1240px] mx-auto">
            <div className="text-center mb-14">
              <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 mb-5">
                <span className="inline-block w-6 h-px align-middle mr-3" style={{ background: GOLD }} />
                Fornitore — 3 passi
              </div>
              <h2 className="font-display text-[34px] sm:text-[44px] tracking-[-0.02em] font-light text-neutral-950">
                Così semplice
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { n: "01", title: "Ti registri in 5 minuti", body: "Candidati con il form qui sotto. Creiamo il tuo account Stripe Express — nessun costo, nessun vincolo, tutto online." },
                { n: "02", title: "Condividi il tuo codice", body: "Ricevi il tuo codice personale (es. DJMARCO). I tuoi clienti sposi lo usano al checkout WedFlow e risparmiano il 5%." },
                { n: "03", title: "Ricevi le commissioni", body: "Il 1° di ogni mese accreditiamo automaticamente il 20% di ogni vendita generata dal tuo codice direttamente su Stripe." },
              ].map(({ n, title, body }) => (
                <div key={n} className="rounded-[14px] bg-white border border-neutral-200 p-8">
                  <div className="font-display text-[48px] font-light tracking-[-0.03em]" style={{ color: GOLD }}>{n}</div>
                  <h3 className="mt-4 font-display text-[20px] font-medium text-neutral-950">{title}</h3>
                  <p className="mt-3 text-[14px] text-neutral-600 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 md:py-28 px-5">
          <div className="max-w-[760px] mx-auto">
            <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 mb-5">
              <span className="inline-block w-6 h-px align-middle mr-3" style={{ background: GOLD }} />
              FAQ
            </div>
            <h2 className="font-display text-[34px] sm:text-[44px] tracking-[-0.02em] font-light text-neutral-950 mb-12">
              Domande frequenti
            </h2>
            <FaqItem q="Quanto guadagno come fornitore?"
              a="Il 20% dell'importo pagato dagli sposi per il piano WedFlow (al netto del 5% di sconto già applicato). Es: Smart €59 → sconto 5% = €56,05 pagati → 20% = €11,21 di commissione." />
            <FaqItem q="Quando ricevo i pagamenti?"
              a="Il 1° di ogni mese vengono accreditati automaticamente tutti i guadagni del mese precedente. Soglia minima €20: sotto questa soglia si accumulano al mese successivo." />
            <FaqItem q="Devo emettere fattura?"
              a="Sì. Stripe genera un report mensile dei transfer ricevuti. Sulla base di questo dovrai emettere fattura o ricevuta a WedFlow per l'importo accreditato." />
            <FaqItem q="Posso smettere quando voglio?"
              a="Sì, nessun vincolo di durata. Puoi sospendere il programma in qualsiasi momento. Le commissioni già maturate vengono comunque erogate." />
            <FaqItem q="Come funziona il canone per le location?"
              a="La location paga un canone annuale e ottiene WedFlow Premium per tutti gli eventi nella sua sede: galleria illimitata, slideshow live, reel AI incluso per ogni coppia. Canone Annuale (fino a 30 eventi): €890 Founding o €1.190 a listino. Unlimited (eventi illimitati): €1.799/anno." />
            <FaqItem q="Cosa succede al rinnovo Founding?"
              a="Il prezzo Founding (€890) è riservato al primo anno per le prime 10 location. Dal secondo anno il rinnovo avviene automaticamente a €1.190/anno, il prezzo di listino standard." />
            <FaqItem q="Qual è la differenza tra Canone Annuale e Unlimited?"
              a="Canone Annuale è pensato per location fino a 30 matrimoni all'anno. Unlimited non ha limiti di eventi ed è indicato per location che ospitano 45+ eventi l'anno — il costo per evento diventa più conveniente." />
          </div>
        </section>

        {/* Form candidatura */}
        <section id="candidati" className="py-20 md:py-28 px-5 bg-neutral-50">
          <div className="max-w-[560px] mx-auto">
            <div className="text-center mb-12">
              <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 mb-5">
                <span className="inline-block w-6 h-px align-middle mr-3" style={{ background: GOLD }} />
                Candidati
              </div>
              <h2 className="font-display text-[34px] sm:text-[44px] tracking-[-0.02em] font-light text-neutral-950">Inizia oggi</h2>
              <p className="mt-4 text-[14px] text-neutral-600">Compila il form e ti contattiamo entro 24 ore.</p>
            </div>
            {submitted ? (
              <div className="text-center py-12">
                <div className="font-display text-[22px] text-neutral-950 mb-3">Richiesta inviata!</div>
                <p className="text-[14px] text-neutral-600">Ti contatteremo entro 24 ore a <strong>{form.email}</strong>.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="flex flex-col gap-1">
                    <span className="sr-only">Nome attività (obbligatorio)</span>
                    <input required placeholder="Nome attività *" value={form.business_name}
                      onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))}
                      className="bg-white border border-neutral-200 rounded-[10px] px-4 py-3 text-[14px] outline-none focus:border-neutral-400 transition" />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="sr-only">Nome referente (obbligatorio)</span>
                    <input required placeholder="Nome referente *" value={form.contact_name}
                      onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))}
                      className="bg-white border border-neutral-200 rounded-[10px] px-4 py-3 text-[14px] outline-none focus:border-neutral-400 transition" />
                  </label>
                </div>
                <label className="flex flex-col gap-1">
                  <span className="sr-only">Email (obbligatoria)</span>
                  <input required type="email" placeholder="Email *" value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="bg-white border border-neutral-200 rounded-[10px] px-4 py-3 text-[14px] outline-none focus:border-neutral-400 transition" />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="sr-only">Tipo di attività</span>
                  <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                    className="bg-white border border-neutral-200 rounded-[10px] px-4 py-3 text-[14px] outline-none focus:border-neutral-400 transition text-neutral-700">
                    <option value="supplier">Fornitore (DJ, fotografo, planner…)</option>
                    <option value="venue">Location (villa, castello, agriturismo…)</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="sr-only">Messaggio (opzionale, max 600 caratteri)</span>
                  <textarea placeholder="Messaggio (opzionale)" value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    rows={3} maxLength={600}
                    className="bg-white border border-neutral-200 rounded-[10px] px-4 py-3 text-[14px] outline-none focus:border-neutral-400 transition resize-none" />
                </label>
                {formError && <p role="alert" className="text-[13px] text-red-600">{formError}</p>}
                <button type="submit" disabled={submitting}
                  className="wf-btn-primary rounded-full py-3.5 text-[13px] tracking-wide disabled:opacity-50">
                  {submitting ? "Invio in corso…" : "Candidati come partner →"}
                </button>
                <p className="text-center text-[12px] text-neutral-500">
                  Oppure scrivi a{" "}
                  <a href={`mailto:${PARTNER_EMAIL}`} className="underline underline-offset-2">{PARTNER_EMAIL}</a>
                </p>
              </form>
            )}
          </div>
        </section>
      </main>

      <footer className="border-t border-neutral-200 py-8 px-5 text-center">
        <div className="text-[12px] tracking-[0.12em] uppercase text-neutral-500">
          © {new Date().getFullYear()} WEDFLOW —{" "}
          <a href="/" className="hover:text-neutral-900 transition-colors">Home</a>
          {" · "}
          <a href="/privacy" className="hover:text-neutral-900 transition-colors">Privacy</a>
        </div>
      </footer>
    </div>
  );
}
